import React from 'react';
import {
  Box,
  Image,
  Text,
  Flex,
  Badge,
  VStack,
  HStack,
} from '@chakra-ui/react';
import { ShopifyProduct, ShopifyVariant } from '../../../../types/shopify';
import { calculateProductPricing, getVariantTags, getProductMainImage } from '../../../../utils/shopifyUtils';
import { ff } from '../../../../theme';

interface ProductCardProps {
  product: ShopifyProduct;
  selectedVariant: ShopifyVariant | null;
  showVariant: boolean;
  onProductClick: () => void;
  availableVariantIds?: string[];
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  selectedVariant,
  showVariant,
  onProductClick,
  availableVariantIds = [],
}) => {
  const variantTags = getVariantTags(selectedVariant);
  const { maxPrice, variantCount } = React.useMemo(
    () => calculateProductPricing(product, selectedVariant, availableVariantIds),
    [product.variants, selectedVariant, availableVariantIds]
  );
  const mainImage = getProductMainImage(product);

  return (
    <Box
      as="button"
      type="button"
      textAlign="left"
      w="100%"
      onClick={onProductClick}
      maxW="250px"
      _hover={{ cursor: 'pointer' }}
      sx={{ '&:hover img': { transform: 'scale(1.02)' } }}
    >
      <Box position="relative" aspectRatio={1} borderRadius="20px" overflow="hidden" bg={ff.grey} borderWidth="1px" borderColor={ff.line}>
        {mainImage && (
          <Image
            src={mainImage.url}
            alt={mainImage.altText}
            objectFit="cover"
            width="100%"
            height="100%"
            transition="transform 0.2s"
          />
        )}
      </Box>
      <VStack align="stretch" pt={2} px={1} spacing={1}>
        <Text fontSize="14px" fontWeight="600" color={ff.ink} noOfLines={1}>
          {product.title}
        </Text>
        {selectedVariant ? (
          <VStack align="stretch" spacing={1}>
            <HStack justify="space-between" width="100%">
              <Text fontSize="13px" color={ff.text2}>
                ${selectedVariant.price} value
              </Text>
            </HStack>
            {variantTags.length > 0 && (
              <Flex wrap="wrap" gap={1} mt={1}>
                {variantTags.map((tag, index) => (
                  <Badge
                    key={index}
                    bg={ff.grey}
                    color={ff.ink}
                    fontSize="11px"
                    fontWeight="500"
                    textTransform="none"
                    px={2}
                    py={0.5}
                    borderRadius="full"
                  >
                    {tag}
                  </Badge>
                ))}
              </Flex>
            )}
          </VStack>
        ) : maxPrice ? <Text fontSize="13px" color={ff.text2}>${maxPrice} value {variantCount > 1 && `• ${variantCount} options`}</Text> : null}
      </VStack>
    </Box>
  );
}; 